import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { FAMILIES, SampleMetaSchema, parseDrone, type SampleMeta, type SourceLayer } from '@tonist/shared';
import { ensureEngineStarted, type EngineWithExtras } from '../../lib/engineBoot';
import { postForm, errorMessage } from '../../lib/api';
import { useAuth } from '../../store/authStore';
import { toast } from '../../store/uiStore';
import { Field } from './EngineForm';
import { getPath, setPath, type FieldSpec } from './fieldSpec';

const KINDS = ['sample', 'freeze', 'synth'] as const;
const SYNTH_ENGINES = ['additive', 'wavetable', 'fm', 'shepard', 'noise'] as const;
const n = (path: string, label: string, min: number, max: number, step = 0.01, unit?: string): FieldSpec => ({ path, label, kind: 'number', min, max, step, unit });

export function defaultSource(kind: SourceLayer['kind'], sampleId = 'sitar'): SourceLayer {
  const d = parseDrone({ id: 'x', name: 'x', sources: [
    kind === 'sample' ? { kind, sampleId } : kind === 'freeze' ? { kind, sampleId } : { kind, engine: 'additive', partials: [1, 0.5, 0.33, 0.25, 0.2, 0.16] },
  ] });
  return d.sources[0]!;
}

const SAMPLE_FIELDS: FieldSpec[] = [
  { path: 'rootMidi', label: 'Root MIDI', kind: 'int', min: 12, max: 108, step: 1 },
  { path: 'pitchMode', label: 'Pitch mode', kind: 'enum', options: ['multisample', 'resample', 'stretch'] },
  { path: 'formantLock', label: 'Formant lock', kind: 'bool' },
  { path: 'loop.mode', label: 'Loop', kind: 'enum', options: ['baked', 'stagger'] },
  n('semitoneOffset', 'Transpose', -36, 36, 1, 'st'), n('levelDb', 'Level', -60, 12, 0.5, 'dB'),
];
const FREEZE_FIELDS: FieldSpec[] = [
  { path: 'rootMidi', label: 'Root MIDI', kind: 'int', min: 12, max: 108, step: 1 },
  n('position', 'Position', 0, 1), n('grain.sizeMs', 'Grain size', 10, 1000, 1, 'ms'), n('grain.density', 'Density', 1, 200, 1, '/s'),
  n('grain.spraySec', 'Spray', 0, 5, 0.01, 's'), n('grain.pitchJitterCents', 'Pitch jitter', 0, 200, 1, '¢'), n('grain.texture', 'Texture', 0, 1),
  n('stretch', 'Stretch', 1, 1000, 1, '×'), n('semitoneOffset', 'Transpose', -36, 36, 1, 'st'), n('levelDb', 'Level', -60, 12, 0.5, 'dB'),
];
const SYNTH_COMMON: FieldSpec[] = [n('semitoneOffset', 'Transpose', -36, 36, 1, 'st'), n('levelDb', 'Level', -60, 12, 0.5, 'dB')];

export function SourceEditor({ engine, sources, errors, onChange }: {
  engine: EngineWithExtras; sources: SourceLayer[]; errors: Record<string, string>; onChange: (sources: SourceLayer[]) => void;
}) {
  const [samples, setSamples] = useState<SampleMeta[]>(() => safeList(engine));
  const refreshSamples = () => engine.samples.refresh().then(() => setSamples(safeList(engine))).catch(() => setSamples(safeList(engine)));
  useEffect(() => { void refreshSamples(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const update = (i: number, layer: SourceLayer) => onChange(sources.map((s, j) => (j === i ? layer : s)));
  return (
    <div className="stack">
      {sources.map((layer, i) => (
        <fieldset key={i} className="group">
          <legend>Source {i + 1}</legend>
          <div className="stack">
            <div className="row">
              <div className="seg" role="group" aria-label="Kind">
                {KINDS.map((k) => <button key={k} type="button" aria-pressed={layer.kind === k} onClick={() => update(i, layer.kind === k ? layer : defaultSource(k, 'sampleId' in layer ? layer.sampleId : undefined))}>{k}</button>)}
              </div>
              <span className="grow" />
              {sources.length > 1 && <button type="button" className="btn btn--sm btn--danger" onClick={() => onChange(sources.filter((_, j) => j !== i))}>Remove</button>}
            </div>
            {layer.kind !== 'synth' && (
              <SamplePicker engine={engine} samples={samples} value={layer.sampleId} error={errors[`sources.${i}.sampleId`]}
                onPick={(id, meta) => update(i, { ...layer, sampleId: id, ...(meta ? { rootMidi: meta.rootMidi } : {}) })}
                onRegistryChange={() => void refreshSamples()} />
            )}
            {layer.kind === 'synth' && (
              <div className="form-grid">
                <Field spec={{ path: 'engine', label: 'Engine', kind: 'enum', options: SYNTH_ENGINES }} value={layer.engine} onChange={(v) => update(i, { ...layer, engine: v as typeof layer.engine })} />
                {layer.engine === 'wavetable' && <Field spec={{ path: 'wavetable', label: 'Wavetable', kind: 'enum', options: ['saw', 'square', 'triangle', 'sine', 'supersaw', 'organ'] }} value={layer.wavetable ?? 'saw'} onChange={(v) => update(i, { ...layer, wavetable: v as NonNullable<typeof layer.wavetable> })} />}
                {layer.engine === 'fm' && <>
                  <Field spec={n('fm.ratio', 'FM ratio', 0.25, 16, 0.25)} value={layer.fm?.ratio ?? 2} onChange={(v) => update(i, { ...layer, fm: { ratio: Number(v), index: layer.fm?.index ?? 2 } })} />
                  <Field spec={n('fm.index', 'FM index', 0, 20, 0.1)} value={layer.fm?.index ?? 2} onChange={(v) => update(i, { ...layer, fm: { ratio: layer.fm?.ratio ?? 2, index: Number(v) } })} />
                </>}
                {layer.engine === 'shepard' && <>
                  <Field spec={n('shepard.rate', 'Rise rate', -2, 2, 0.01)} value={layer.shepard?.rate ?? 0.2} onChange={(v) => update(i, { ...layer, shepard: { rate: Number(v), octaves: layer.shepard?.octaves ?? 6 } })} />
                  <Field spec={{ path: 'shepard.octaves', label: 'Octaves', kind: 'int', min: 3, max: 8, step: 1 }} value={layer.shepard?.octaves ?? 6} onChange={(v) => update(i, { ...layer, shepard: { rate: layer.shepard?.rate ?? 0.2, octaves: Number(v) } })} />
                </>}
                {layer.engine === 'additive' && (
                  <div className="field" style={{ gridColumn: '1 / -1' }}>
                    <label>Partials (1–32 amplitudes, comma separated)</label>
                    <input className="input" value={(layer.partials ?? []).map((p) => +p.toFixed(3)).join(', ')}
                      onChange={(e) => update(i, { ...layer, partials: e.target.value.split(',').map((s) => Number(s.trim())).filter((x) => Number.isFinite(x)).slice(0, 32) })} />
                    {errors[`sources.${i}.partials`] && <span className="error">{errors[`sources.${i}.partials`]}</span>}
                  </div>
                )}
                {SYNTH_COMMON.map((f) => <Field key={f.path} spec={f} value={getPath(layer, f.path)} error={errors[`sources.${i}.${f.path}`]} onChange={(v) => update(i, setPath(layer, f.path, v))} />)}
              </div>
            )}
            {layer.kind !== 'synth' && (
              <div className="form-grid">
                {(layer.kind === 'sample' ? SAMPLE_FIELDS : FREEZE_FIELDS).map((f) => (
                  <Field key={f.path} spec={f} value={getPath(layer, f.path)} error={errors[`sources.${i}.${f.path}`]} onChange={(v) => update(i, setPath(layer, f.path, v))} />
                ))}
              </div>
            )}
          </div>
        </fieldset>
      ))}
      {sources.length < 3 && <div><button type="button" className="btn btn--sm" onClick={() => onChange([...sources, defaultSource('synth')])}>Add a source layer</button></div>}
    </div>
  );
}

function safeList(engine: EngineWithExtras): SampleMeta[] {
  try { return engine.samples.list(); } catch { return []; }
}

function SamplePicker({ engine, samples, value, error, onPick, onRegistryChange }: {
  engine: EngineWithExtras; samples: SampleMeta[]; value: string; error?: string; onPick: (id: string, meta?: SampleMeta) => void; onRegistryChange: () => void;
}) {
  const [family, setFamily] = useState<string>('');
  const user = useAuth((s) => s.user);
  const list = useMemo(() => samples.filter((s) => !family || s.family === family), [samples, family]);
  const preview = useRef<{ release(): void } | null>(null);

  const play = async (id: string) => {
    try {
      await ensureEngineStarted(engine);
      const meta = engine.samples.get(id);
      const drone = parseDrone({ id: `__preview_${id}`, name: `preview ${id}`, sources: [{ kind: 'sample', sampleId: id, rootMidi: meta?.rootMidi ?? 49 }] });
      await engine.loadDrone(drone);
      preview.current?.release();
      const h = engine.noteOn(drone.id, meta?.rootHz ?? 138.59, { radius: 0.5, velocity: 0 });
      preview.current = h;
      setTimeout(() => { if (preview.current === h) { h.release(); preview.current = null; } }, 2500);
    } catch (e) { toast(errorMessage(e), 'error'); }
  };

  return (
    <div className="stack">
      <div className="row">
        <select className="select" style={{ width: 'auto' }} aria-label="Family filter" value={family} onChange={(e) => setFamily(e.target.value)}>
          <option value="">All families</option>
          {[...new Set(samples.map((s) => s.family).filter(Boolean))].map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select className="select grow" aria-label="Sample" value={value} onChange={(e) => onPick(e.target.value, engine.samples.get(e.target.value))}>
          {!list.some((s) => s.id === value) && <option value={value}>{value}</option>}
          {list.map((s) => <option key={s.id} value={s.id}>{s.name}{s.builtIn ? '' : ' (mine)'}{s.nc ? ' · NC' : ''}</option>)}
        </select>
        <button type="button" className="btn btn--sm" onClick={() => void play(value)} aria-label={`Preview ${value}`}>▶ preview</button>
      </div>
      {error && <span className="error">{error}</span>}
      <Uploader engine={engine} signedIn={!!user} onDone={(id) => { onRegistryChange(); onPick(id, engine.samples.get(id)); }} />
    </div>
  );
}

function Uploader({ engine, signedIn, onDone }: { engine: EngineWithExtras; signedIn: boolean; onDone: (id: string) => void }) {
  const [rights, setRights] = useState(false);
  const [travel, setTravel] = useState(true);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const rec = useRef<{ mr: MediaRecorder; chunks: Blob[]; stream: MediaStream } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const upload = async (file: File) => {
    if (!rights) { toast('Please confirm you have the rights to this recording', 'error'); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      form.append('rights', 'true');
      form.append('mayTravel', String(travel));
      const res = await postForm<unknown>('/samples', form);
      const meta = SampleMetaSchema.safeParse((res as { item?: unknown })?.item ?? (res as { sample?: unknown })?.sample ?? res);
      await engine.samples.refresh();
      if (meta.success) onDone(meta.data.id); else toast('Uploaded', 'info');
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };

  const registerLocal = async (blob: Blob, name: string) => {
    setBusy(true);
    try {
      await ensureEngineStarted(engine);
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) throw new Error('Web Audio is not available');
      const ctx = new Ctx();
      const buffer = await ctx.decodeAudioData(await blob.arrayBuffer());
      await ctx.close();
      const id = `local-${Date.now().toString(36)}`;
      const meta: SampleMeta = {
        id, name, family: '', url: '', durationMs: Math.round(buffer.duration * 1000), sampleRate: buffer.sampleRate, channels: buffer.numberOfChannels,
        loopStart: 0, loopEnd: buffer.length, rootHz: 138.59, rootMidi: 49, builtIn: false, ownerId: null, license: 'mine', credit: '', sourceUrl: '', nc: false, unpitched: false,
      };
      engine.samples.registerLocal(meta, buffer);
      onDone(id);
      toast(`${name} is ready for this session`);
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } });
      const mr = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
        rec.current = null; setRecording(false);
        if (signedIn && rights) void upload(new File([blob], `recording.${(mr.mimeType || 'audio/webm').split('/')[1]?.split(';')[0] ?? 'webm'}`, { type: blob.type }));
        else void registerLocal(blob, 'My recording');
      };
      rec.current = { mr, chunks, stream };
      mr.start();
      setRecording(true);
      setTimeout(() => { if (rec.current?.mr === mr && mr.state === 'recording') mr.stop(); }, 30_000);
    } catch (e) { toast(errorMessage(e, 'Microphone unavailable'), 'error'); }
  };
  const stopRecording = () => { rec.current?.mr.stop(); };

  return (
    <details className="disclosure">
      <summary style={{ fontSize: 14 }}>Upload or record your own</summary>
      <div className="stack">
        <label className="check"><input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)} /> I have the rights to this recording</label>
        <label className="check"><input type="checkbox" checked={travel} onChange={(e) => setTravel(e.target.checked)} /> It may travel with drones I share</label>
        <div className="row">
          {signedIn
            ? <>
              <input ref={fileRef} type="file" accept="audio/*" className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }} aria-label="Choose an audio file" />
              <button type="button" className="btn btn--sm" disabled={busy} onClick={() => fileRef.current?.click()}>Upload a file (≤ 30 s)</button>
            </>
            : <span className="hint"><Link to="/auth">Sign in</Link> to upload — or record below; recordings stay in this browser session.</span>}
          {!recording
            ? <button type="button" className="btn btn--sm" disabled={busy} onClick={() => void startRecording()}>● Record</button>
            : <button type="button" className="btn btn--sm btn--danger" onClick={stopRecording}>■ Stop</button>}
          {busy && <span className="hint">working…</span>}
        </div>
        <FamiliesNote />
      </div>
    </details>
  );
}

function FamiliesNote() {
  return <div className="hint">Families: {FAMILIES.join(', ')}. Uploads are normalised, looped and stored as FLAC.</div>;
}
